import React from 'react';
import { Link } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';

const AlumniUserAgreement = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            className="mx-auto h-16 w-auto sm:h-20 mb-4"
            src={nacmeLogo}
            alt="NACME Logo"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            NACME Alumni Network User Agreement
          </h1>
          <p className="text-gray-600">
            Effective Date: September 15, 2025 | Last Updated: September 15, 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
          <div className="prose prose-blue max-w-none">
            
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-6">
              By accessing or using the NACME Alumni Network platform ("Platform"), you agree to be bound by this User Agreement ("Agreement") and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this Platform.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
            <p className="mb-4">To use the NACME Alumni Network, you must:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Be a verified NACME scholar or alumnus/a</li>
              <li>Be at least 18 years of age</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security and confidentiality of your account credentials</li>
              <li>Have the legal capacity to enter into this Agreement</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration and Security</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">3.1 Account Creation</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>You must provide truthful and accurate information during registration</li>
              <li>You may not impersonate any person or entity</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">3.2 Account Termination</h3>
            <p className="mb-6">We reserve the right to suspend or terminate accounts that:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Violate this Agreement or our Community Guidelines</li>
              <li>Contain false or misleading information</li>
              <li>Engage in fraudulent or illegal activities</li>
              <li>Remain inactive for extended periods</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Content and Conduct</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 Content Ownership</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>You retain ownership of all content you post on the Platform</li>
              <li>By posting content, you grant NACME a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content on the Platform</li>
              <li>You represent that you have the right to post any content you share</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 Prohibited Content</h3>
            <p className="mb-2">You may not post content that:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Is false, misleading, or deceptive</li>
              <li>Infringes on intellectual property rights</li>
              <li>Contains malware, viruses, or harmful code</li>
              <li>Violates any applicable laws or regulations</li>
              <li>Is defamatory, harassing, or discriminatory</li>
              <li>Contains sensitive personal information of others without consent</li>
              <li>Promotes illegal activities or violence</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 Professional Standards</h3>
            <p className="mb-2">As this is a professional networking platform, you agree to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Maintain professional decorum in all interactions</li>
              <li>Respect the privacy and boundaries of other users</li>
              <li>Use the Platform for legitimate professional networking purposes</li>
              <li>Not engage in spam, excessive self-promotion, or commercial solicitation without authorization</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Privacy and Data Protection</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 Data Collection and Use</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>We collect and process your data in accordance with our Privacy Policy</li>
              <li>You consent to the collection and use of your information as outlined in the Privacy Policy</li>
              <li>You have the right to control the visibility of your profile information through privacy settings</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 Social Network Integration</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>If you choose to import your professional profile from third-party social network platforms, you authorize us to access and store that information</li>
              <li>Imported data remains subject to this Agreement and our Privacy Policy</li>
              <li>You may update or remove imported data at any time through your account settings</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">5.3 Data Sharing</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Your profile may be visible to recruiters, other alumni, and NACME administrators based on your privacy settings</li>
              <li>You control which information is publicly visible through your privacy preferences</li>
              <li>We do not sell your personal information to third parties</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Networking and Communication Features</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 Connections</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Connection requests must be made in good faith for professional networking purposes</li>
              <li>You may not harass other users through excessive or inappropriate connection requests</li>
              <li>Users have the right to accept, decline, or block connection requests</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 Messaging</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Messages must be professional and respectful</li>
              <li>Spam, harassment, or inappropriate solicitation is prohibited</li>
              <li>Users may report inappropriate messages for review</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 Group Participation</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Group discussions must remain relevant to the group's purpose</li>
              <li>Group administrators have the right to moderate content and membership</li>
              <li>Respect diverse opinions and maintain professional discourse</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Job Board and Recruitment Features</h2>
            <h3 className="text-lg font-medium text-gray-800 mb-2">7.1 Job Postings</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Recruiters must post legitimate job opportunities only</li>
              <li>Job postings must comply with all applicable employment laws</li>
              <li>Discriminatory job postings are strictly prohibited</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">7.2 Applications</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Alumni applying to positions must provide accurate information</li>
              <li>Misrepresentation of qualifications or experience is grounds for account termination</li>
              <li>Application data may be shared with prospective employers</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimers and Limitations of Liability</h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, NACME SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Dispute Resolution</h2>
            <p className="mb-4">
              This Agreement shall be governed by the laws of Virginia, without regard to conflict of law principles. Any disputes arising from this Agreement shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
            <p className="mb-6">
              For questions or concerns about this Agreement, please contact:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="font-medium">NACME Alumni Network Support</p>
              <p>Email: admin@nacme.app</p>
              <p>Address: 1432 Duke Street, Alexandria, VA 22314</p>
            </div>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-600 italic">
                By using the NACME Alumni Network, you acknowledge that you have read, understood, and agree to be bound by this User Agreement.
              </p>
              <p className="text-sm text-gray-600 italic mt-2">
                This Agreement was last updated on September 15, 2025. We recommend reviewing it periodically for any changes.
              </p>
            </div>
          </div>

          {/* Back to Registration Button */}
          <div className="mt-8 text-center">
            <Link
              to="/alumni/register"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              ← Back to Registration
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniUserAgreement;